import Async from "components/DesignSystem/AsyncComponents/Async";
import { Button } from "components/DesignSystem/Button/Button";
import { FormikForm } from "components/FormikForm/FormikForm";
import Modal from "components/DesignSystem/Modal/Modal";
import { ITEM_TYPES } from "constants/productAndServices";
import { PRICE_FREQUENCY } from "constants/productPriceFrequency";
import { PRODUCT_SERVICE_ITEM_TYPE } from "dictionaries";
import { addProductAndServiceSchema } from "formValidations/productAndServiceSchema";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useToast } from "hooks/useToast";
import {
  useAddProductAndServiceMutation,
  useGetProductAndServiceByIdQuery,
  useUpdateProductAndServiceMutation,
} from "store/apis/productAndServices";
import {
  ItemType,
  ProductAndServices,
  ProductAndServicesDetail,
} from "types/Models/productAndServices";
import { BackendError } from "types/utils/error";
import { ProductFormFields } from "./ProductFormFields";

type ProductFormValues = {
  name: string;
  type: ItemType;
  product_category: string;
  linked_price_items: {
    uuid?: string;
    price: number;
    description: string;
    is_default: boolean;
    frequency: string;
  }[];
  transaction_category_id: string | null;
  classification_ids: string[];
  default_tax: string | null;
  sales_tax_code: string | null;
};

export const AddProductModalContent = ({
  close,
  name,
  onSuccess,
}: {
  close: () => void;
  name?: string;
  onSuccess?: ({ item }: { item: ProductAndServicesDetail }) => void;
}) => {
  const { alertToast, successToast } = useToast();
  const entityId = useCurrentEntityId();
  const { uuid: groupId } = useCurrentGroupContext();

  const [addProductAndService, { isLoading: isAdding }] =
    useAddProductAndServiceMutation();

  const onSubmit = async (values: ProductFormValues) => {
    try {
      const { type, ...rest } = await addProductAndService({
        groupId,
        entityId,
        payload: {
          name: values.name,
          type: values.type,
          product_category: values.product_category,
          prices: values.linked_price_items,
          transaction_category_id: values.transaction_category_id,
          classification_ids: values.classification_ids,
          default_tax_rate_id: values.default_tax,
          sales_tax_code_id: values.sales_tax_code,
        },
      }).unwrap();
      onSuccess?.({ item: { type, ...rest } });
      successToast({ message: `${PRODUCT_SERVICE_ITEM_TYPE[type]} added` });
      close();
    } catch (error) {
      alertToast(
        { message: (error as BackendError).data?.error?.message },
        error as {}
      );
    }
  };

  const initialValues: ProductFormValues = {
    type: ITEM_TYPES.PRODUCT,
    product_category: "",
    linked_price_items: [
      {
        price: 0,
        description: "",
        is_default: true,
        frequency: PRICE_FREQUENCY.ONE_TIME,
      },
    ],
    transaction_category_id: null,
    classification_ids: [],
    default_tax: null,
    sales_tax_code: null,
    name: name || "",
  };

  return (
    <Modal.Content asChild useCustomOverlay size="xxl">
      <FormikForm
        initialValues={initialValues}
        onSubmit={onSubmit}
        validationSchema={addProductAndServiceSchema}
      >
        {({ submitForm }) => (
          <>
            <Modal.Header className="sm:t-pl-4">
              <Modal.Title>Add Product</Modal.Title>
              <Modal.Close />
            </Modal.Header>
            <Modal.Body className="t-flex t-gap-4 t-flex-col sm:t-px-4">
              <ProductFormFields />
            </Modal.Body>
            <Modal.FooterButtonGroup>
              <Modal.RawClose asChild disabled={isAdding}>
                <Button>Cancel</Button>
              </Modal.RawClose>
              <Button
                customType="primary"
                isLoading={isAdding}
                disabled={isAdding}
                onClick={submitForm}
              >
                Add product
              </Button>
            </Modal.FooterButtonGroup>
          </>
        )}
      </FormikForm>
    </Modal.Content>
  );
};

export const EditProductModalContent = ({
  close,
  itemId,
}: {
  close: () => void;
  itemId: string;
}) => {
  const { alertToast, successToast } = useToast();
  const entityId = useCurrentEntityId();
  const { uuid: groupId } = useCurrentGroupContext();

  const {
    data: productOrService,
    isSuccess,
    isLoading,
  } = useGetProductAndServiceByIdQuery(
    { productId: itemId, entityId, groupId },
    {
      skip: !itemId || !entityId || !groupId,
      refetchOnMountOrArgChange: true,
    }
  );

  const [updateProductAndService, { isLoading: isUpdating }] =
    useUpdateProductAndServiceMutation();

  const {
    name = "",
    type = ITEM_TYPES.PRODUCT,
    product_category_id = "",
    transaction_category_id = "",
    source,
    linked_price_list = [],
    classification = [],
    default_tax_rate_id = null,
    default_tax_rate = null,
    sales_tax_code_id = null,
    sales_tax_code = null,
  } = productOrService || {};

  const isStripeProduct = source === "STRIPE";

  const classificationIds = classification.flatMap((group) =>
    group.classes.map((cls) => cls.uuid)
  );

  const initialLinkedPrices =
    linked_price_list.length > 0
      ? linked_price_list
      : [
          {
            price: 0,
            description: "",
            is_default: true,
            frequency: PRICE_FREQUENCY.ONE_TIME,
          },
        ];

  const onSubmit = async (values: ProductFormValues) => {
    try {
      const { type } = await updateProductAndService({
        groupId,
        entityId,
        productId: itemId,
        payload: {
          name: values.name,
          type: values.type,
          product_category: values.product_category,
          prices: values.linked_price_items,
          transaction_category_id: values.transaction_category_id || undefined,
          classification_ids: values.classification_ids,
          default_tax_rate_id: values.default_tax,
          sales_tax_code_id: values.sales_tax_code,
        },
      }).unwrap();
      successToast({
        message: `${PRODUCT_SERVICE_ITEM_TYPE[type]} updated!`,
      });
      close();
    } catch (error) {
      alertToast(
        { message: (error as BackendError).data?.error?.message },
        error as {}
      );
    }
  };

  return (
    <Modal.Content asChild useCustomOverlay size="xxl">
      <FormikForm
        initialValues={{
          name,
          type,
          product_category: product_category_id,
          linked_price_items: initialLinkedPrices,
          transaction_category_id,
          classification_ids: classificationIds,
          default_tax: default_tax_rate_id || default_tax_rate?.uuid || null,
          sales_tax_code: sales_tax_code_id || sales_tax_code?.uuid || null,
        }}
        onSubmit={onSubmit}
        validationSchema={addProductAndServiceSchema}
        enableReinitialize
      >
        {({ submitForm }) => (
          <Async.Root
            isLoading={isLoading}
            isSuccess={isSuccess}
            isEmpty={!productOrService}
          >
            <Async.Empty>
              <></>
            </Async.Empty>
            <Async.Success key={itemId}>
              <Modal.Header className="sm:t-pl-4">
                <Modal.Title>
                  Edit {PRODUCT_SERVICE_ITEM_TYPE[type]}
                </Modal.Title>
                <Modal.Close />
              </Modal.Header>
              <Modal.Body className="t-flex t-gap-4 t-flex-col sm:t-px-4">
                <ProductFormFields isStripeProduct={isStripeProduct} />
              </Modal.Body>
              <Modal.FooterButtonGroup>
                <Modal.RawClose asChild>
                  <Button>Cancel</Button>
                </Modal.RawClose>
                <Button
                  customType="primary"
                  isLoading={isUpdating}
                  disabled={isUpdating}
                  onClick={submitForm}
                >
                  Save
                </Button>
              </Modal.FooterButtonGroup>
            </Async.Success>
          </Async.Root>
        )}
      </FormikForm>
    </Modal.Content>
  );
};

export const ProductFormModalRoot = ({
  isOpen,
  close,
  mode,
  itemId,
  name,
  onSuccess,
}: {
  isOpen: boolean;
  close: () => void;
  mode: "add" | "edit";
  itemId?: string;
  name?: string;
  onSuccess?: ({ item }: { item: ProductAndServices }) => void;
}) => {
  if (!isOpen) {
    return null;
  }

  if (mode === "add") {
    return (
      <Modal.Root open={isOpen} onOpenChange={close}>
        <AddProductModalContent
          close={close}
          name={name}
          onSuccess={onSuccess}
        />
      </Modal.Root>
    );
  }

  return (
    <Modal.Root open={isOpen} onOpenChange={close}>
      <EditProductModalContent close={close} itemId={itemId!} />
    </Modal.Root>
  );
};
